<!-- Login unificado: el backend identifica el destino por el role del usuario. -->
<template>
  <main class="login-page">
    <div class="login-page__decor login-page__decor--orb-one" aria-hidden="true"></div>
    <div class="login-page__decor login-page__decor--orb-two" aria-hidden="true"></div>
    <div class="login-page__dots login-page__dots--top" aria-hidden="true"></div>
    <div class="login-page__dots login-page__dots--bottom" aria-hidden="true"></div>

    <router-link to="/" class="login-back-button">
      <v-icon small>mdi-arrow-left</v-icon>
      <span>Regresar</span>
    </router-link>

    <div class="login-layout">
      <section class="login-card" aria-labelledby="login-card-title">
        <div class="login-card__header">
          <span
            class="login-mark"
            :class="{ 'login-mark--association': associationProfilePicture }"
            aria-label="WeChamber"
          >
            <transition name="login-mark-flip" mode="out-in">
              <img
                :key="associationProfilePicture || defaultProfilePicture"
                :src="associationProfilePicture || defaultProfilePicture"
                alt=""
                @error="handleAssociationImageError"
              />
            </transition>
          </span>
          <h2 id="login-card-title">¡Hola!</h2>
          <p>Inicia sesión para continuar</p>
        </div>

        <form class="login-form" @submit.prevent="_login">
          <div class="login-field">
            <label for="login-email">Correo electrónico</label>
            <div
              class="login-field__control"
              :class="{ 'login-field__control--error': emailError || emailLookupError }"
            >
              <v-icon aria-hidden="true">mdi-email-outline</v-icon>
              <input
                id="login-email"
                v-model.trim="dataUser.email"
                type="email"
                autocomplete="email"
                placeholder="ejemplo@correo.com"
                @blur="validateField('email')"
                @input="handleEmailInput"
                @keyup.enter="_login"
              />
            </div>
            <span v-if="emailError" class="login-field__error">{{ emailError }}</span>
            <span v-else-if="emailLookupError" class="login-field__error">{{ emailLookupError }}</span>
          </div>

          <div class="login-field">
            <label for="login-password">Contraseña</label>
            <div
              class="login-field__control"
              :class="{ 'login-field__control--error': passwordError }"
            >
              <v-icon aria-hidden="true">mdi-lock-outline</v-icon>
              <input
                id="login-password"
                v-model="dataUser.password"
                :type="showPass ? 'text' : 'password'"
                autocomplete="current-password"
                placeholder="Ingresa tu contraseña"
                @blur="validateField('password')"
                @input="passwordError = ''"
                @keyup.enter="_login"
              />
              <button
                type="button"
                class="login-field__toggle"
                :aria-label="showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                @click="showPass = !showPass"
              >
                <v-icon aria-hidden="true">
                  {{ showPass ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}
                </v-icon>
              </button>
            </div>
            <span v-if="passwordError" class="login-field__error">{{ passwordError }}</span>
          </div>

          <div class="login-options">
            <label class="login-remember">
              <input v-model="rememberMe" type="checkbox" />
              <span class="login-remember__box" aria-hidden="true"></span>
              <span>Recuérdame</span>
            </label>
            <button type="button" class="login-forgot" @click="passwordRecover">
              ¿Has olvidado tu contraseña?
            </button>
          </div>

          <button
            type="submit"
            class="login-submit"
            :disabled="isLoginDisabled"
          >
            <span>Iniciar sesión</span>
            <v-progress-circular
              v-if="loadingButton"
              indeterminate
              color="#1b273e"
              size="18"
              width="2"
            />
            <v-icon v-else aria-hidden="true">mdi-arrow-right</v-icon>
          </button>

          <div class="login-divider" aria-hidden="true">
            <span></span>
            <small>o continúa con</small>
            <span></span>
          </div>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span class="login-google-wrapper" v-bind="attrs" v-on="on">
                <button
                  type="button"
                  class="login-google"
                  disabled
                  aria-disabled="true"
                  aria-label="Continuar con Google. Próximamente"
                >
                  <img
                    class="login-google__mark"
                    src="@/assets/images/weChamber/google.png"
                    alt=""
                    aria-hidden="true"
                  />
                  <span>Continuar con Google</span>
                </button>
              </span>
            </template>
            <span>Próximamente</span>
          </v-tooltip>
        </form>
      </section>

      <p class="login-terms">
        Al iniciar sesión, aceptas nuestros
        <router-link to="/terminos-y-condiciones">Términos y condiciones</router-link>
        y
        <router-link to="/aviso-de-privacidad">Política de privacidad</router-link>
      </p>
    </div>

    <!-- Dialog que muestra el estatus del usuario -->
    <v-dialog v-model="userStatusDialog" width="600">
      <v-card>
        <v-card-text>
          <v-row justify="center">
            <v-col cols="10" md="7" class="mt-8 d-flex justify-center">
              <v-img
                src="@/assets/images/weChamber/logo-WC-main-nav-drawer.svg"
                max-width="220"
                height="auto"
                contain
              ></v-img>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="mt-2">
              <p
                v-if="userStatus == 'TO_VALIDATE'"
                class="text-center font-weight-bold headline mb-0"
              >
                Validación de correo
              </p>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <p v-if="userStatus == 'TO_VALIDATE'" class="title text-center">
                El correo del usuario aún no está validado, puede validarlo
                enviando un correo de validación con el botón de "Validar
                correo".
              </p>
              <p v-if="userStatus == 'SUSPENDED'" class="title text-center">
                El usuario ha sido suspendido, no puedes iniciar sesión.
              </p>
              <p v-if="userStatus == 'DELETED'" class="title text-center">
                El usuario ha sido eliminado, no puedes iniciar sesión.
              </p>
            </v-col>
          </v-row>
          <v-row
            class="d-flex pb-2 mt-5"
            :class="$vuetify.breakpoint.smAndUp ? 'justify-end' : 'justify-center'"
          >
            <v-btn
              class="font-weight-bold black--text mx-2"
              color="grey lighten-3"
              rounded
              elevation="0"
              @click="userStatusDialog = false"
            >
              Cerrar
            </v-btn>
            <v-btn
              v-if="userStatus == 'TO_VALIDATE'"
              class="font-weight-bold black--text mx-2"
              color="accent"
              rounded
              elevation="0"
              @click="$_resendEmailValidation"
            >
              Validar correo
            </v-btn>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </main>
</template>

<script>
import { mapMutations, mapActions, mapState } from "vuex";
import { validations } from "@/share/validations.js";
import { colors } from "@/constants/colors.js";
import { isAmpiMember } from "@/common/memberDashboardRouting";
import Endpoints from "@/share/Endpoints";
import defaultProfilePicture from "@/assets/images/weChamber/logo-WC-mark.svg";

export default {
  name: "UnifiedLogin",
  data() {
    return {
      loadingButton: false,
      validSignInForm: false,
      showPass: false,
      rememberMe: false,
      dataUser: {
        email: "",
      password: "",
      },
      emailError: "",
      emailLookupError: "",
      emailLookupValid: null,
      emailLookupTimer: null,
      emailLookupRequest: 0,
      associationProfilePicture: "",
      defaultProfilePicture,
      passwordError: "",
      userStatus: "",
      userStatusDialog: false,
      emailRules: validations.emailValidation({ required: true }),
      passwordRules: validations.generalFValidation({
        required: true,
        name: "contraseña",
      }),
      color: colors,
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    isLoginDisabled() {
      return (
        !this.dataUser.email ||
        !this.dataUser.password ||
        this.emailLookupValid !== true ||
        this.loadingButton
      );
    },
  },
  methods: {
    ...mapActions("authentication", ["resendEmailValidation"]),
    ...mapActions("user", ["login", "realoadUserData", "botTemporalAccess"]),
    ...mapMutations("notification", ["show"]),
    ...mapMutations("user", ["setToken"]),
    ...mapMutations("loader", ["loading", "loaded"]),
    validateField(field) {
      const rules = field === "email" ? this.emailRules : this.passwordRules;
      const value = this.dataUser[field];
      const error = rules.map((rule) => rule(value)).find((result) => result !== true);

      this[`${field}Error`] = error || "";
      this.validSignInForm = !this.emailError && !this.passwordError;
      return !error;
    },
    validateSignInForm() {
      const emailValid = this.validateField("email");
      const passwordValid = this.validateField("password");
      return emailValid && passwordValid;
    },
    handleEmailInput() {
      this.emailError = "";
      this.emailLookupError = "";
      this.emailLookupValid = null;
      this.associationProfilePicture = "";
      clearTimeout(this.emailLookupTimer);

      const email = this.dataUser.email;
      const emailValid = this.emailRules.every((rule) => rule(email) === true);
      if (!emailValid) {
        return;
      }

      this.emailLookupTimer = setTimeout(() => {
        this.validateEmailAvailability(email);
      }, 350);
    },
    async validateEmailAvailability(email = this.dataUser.email) {
      const requestId = ++this.emailLookupRequest;
      this.emailLookupValid = null;
      this.emailLookupError = "";

      console.info("[login-association][frontend-request]", {
        requestId,
        email: email.replace(/^(.{2}).*(@.*)$/, "$1***$2"),
      });

      try {
        const { data } = await this.axios.get(
          `${Endpoints.validateLoginEmail}?email=${encodeURIComponent(email)}`,
        );

        if (requestId !== this.emailLookupRequest || email !== this.dataUser.email) {
          return false;
        }

        this.emailLookupValid = data.valid === true;
        this.associationProfilePicture = data.associationProfilePicture || "";
        console.info("[login-association][frontend-response]", {
          requestId,
          valid: this.emailLookupValid,
          hasAssociationProfilePicture: Boolean(this.associationProfilePicture),
          associationProfilePicture: this.associationProfilePicture || null,
        });
        if (!this.emailLookupValid) {
          this.emailLookupError = "Este nombre de usuario no es válido";
        }
        return this.emailLookupValid;
      } catch (error) {
        console.error("[login-association][frontend-error]", {
          requestId,
          status: error?.response?.status || null,
          message: error?.message || "unknown error",
        });
        if (requestId === this.emailLookupRequest) {
          this.emailLookupValid = false;
          this.associationProfilePicture = "";
          this.emailLookupError = "No se pudo validar el nombre de usuario";
        }
        return false;
      }
    },
    handleAssociationImageError() {
      console.error("[login-association][image-error]", {
        associationProfilePicture: this.associationProfilePicture || null,
      });
      this.associationProfilePicture = "";
    },
    async _login() {
      if (!this.validateSignInForm()) {
        return;
      }

      if (this.emailLookupValid !== true) {
        const emailIsValid = await this.validateEmailAvailability();
        if (!emailIsValid) return;
      }

      this.loadingButton = true;

      try {
        const response = await this.login(this.dataUser);
        if (!response.status) {
          this.show({
            text: response.message,
            color: "error",
            time: 8000,
          });
          return;
        }

        this.$gtag.set({ "Tipo de Usuario": response.role });

        const eventRedirect = this.$route.query.event || null;
        const requestedRedirect = this.$route.query.redirect;
        const memberRedirect =
          typeof requestedRedirect === "string" &&
          requestedRedirect.startsWith("/member-v2/")
            ? requestedRedirect
            : null;
        const isAmpiUser = isAmpiMember(this.user);
        const isMembershipUser = response.role === "MEMBERSHIP_USER";
        const isNotAmpiMember = response.role === "MEMBER" && !isAmpiUser;

        if (isAmpiUser) {
          if (eventRedirect) {
            this.$router.push(`/member/eventMicrosite/${eventRedirect}`);
          } else if (response.userState === "PROSPECTO") {
            this.$router.push("/member/home");
          } else if (response.userState === "ACTIVE" && memberRedirect) {
            this.$router.push(memberRedirect);
          } else {
            this.$router.push("/member/affiliation-profile");
          }
          return;
        }

        if (isMembershipUser || isNotAmpiMember) {
          if (eventRedirect) {
            this.$router.push(`/member/eventMicrosite/${eventRedirect}`);
          } else if (response.userState === "ACTIVE" && memberRedirect) {
            this.$router.push(memberRedirect);
          } else {
            this.$router.push("/member/home");
          }
          return;
        }

        if (response.userState === "VALIDATED") {
          this.$router.push("/panel-inicio");
        } else {
          this.userStatus = response.userState;
          this.userStatusDialog = true;
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.loadingButton = false;
      }
    },
    async $_resendEmailValidation() {
      const response = await this.resendEmailValidation(this.dataUser.email);
      this.userStatusDialog = false;
      console.log("Esta es la respuesta");
      console.log(response);
    },
    passwordRecover() {
      this.$router.push("/recuperar-contrasena");
    },
  },
  async created() {
    if (this.$route.params.token && this.$route.params.email) {
      this.loading();

      await this.botTemporalAccess({
        token: this.$route.params.token,
        id: this.$route.params.email,
      });

      const reloadResponse = await this.realoadUserData({ id: this.$route.params.email });

      this.loaded();

      if (!reloadResponse?.status) {
        this.$router.push("/login");
        return;
      }

      if (this.$route.params.eventId && reloadResponse.userState === "ACTIVE") {
        this.$router.push(`/member/eventMicrosite/${this.$route.params.eventId}`);
        return;
      }

      if (this.$route.params.eventId) {
        this.$router.push(`/micrositio/${this.$route.params.eventId}`);
        return;
      }

      const routes = isAmpiMember(this.user)
        ? {
            homeLogin: "/member/home",
            communityLogin: "/member/community",
            benefitsLogin: "/member/benefits",
            myInfoLogin: "/member/home/showInfo",
            eventsLogin: "/member/events",
          }
        : {
            homeLogin: "/member-v2/home",
            communityLogin: "/member-v2/community",
            benefitsLogin: "/member-v2/benefits",
            myInfoLogin: "/member-v2/profile",
            eventsLogin: "/member-v2/events",
          };

      if (this.$route.name in routes) {
        this.$router.push(routes[this.$route.name]);
      } else {
        const link = `/member/renew/2/${this.user._id}`;
        const branch = this.user.branchId;
        this.$router.push(
          `${link}${
            !["N/D", "N/A", null].includes(branch) &&
            this.user.headOffice.paymentType != "NATIONAL"
              ? "/" + branch
              : ""
          }`
        );
      }
    }
  },
  mounted() {
    this.$gtag.screenview({
      app_name: "App",
      screen_name: "Login page",
    });
  },
  beforeDestroy() {
    clearTimeout(this.emailLookupTimer);
    this.emailLookupRequest += 1;
  },
};
</script>

<style scoped>
.login-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background:
    radial-gradient(circle at 63% 80%, rgba(224, 237, 255, 0.35), transparent 25%),
    linear-gradient(110deg, #f7faff 0%, #ffffff 52%, #f9fafc 100%);
  color: #1b273e;
}

.login-page__decor,
.login-page__dots {
  pointer-events: none;
  position: absolute;
}

.login-page__decor--orb-one {
  width: 840px;
  height: 840px;
  top: -475px;
  left: -445px;
  border-radius: 50%;
  background: radial-gradient(circle at 65% 68%, rgba(255, 255, 255, 0.94) 0 49%, rgba(231, 242, 255, 0.64) 50% 72%, rgba(214, 233, 255, 0.22) 73% 100%);
}

.login-page__decor--orb-two {
  width: 560px;
  height: 560px;
  right: -310px;
  bottom: -310px;
  border-radius: 50%;
  border: 1px solid rgba(220, 234, 255, 0.65);
  box-shadow: 0 0 0 48px rgba(233, 242, 255, 0.3), 0 0 0 112px rgba(241, 246, 255, 0.38);
}

.login-page__dots {
  width: 155px;
  height: 132px;
  opacity: 0.55;
  background-image: radial-gradient(#c4e2ff 1.2px, transparent 1.2px);
  background-size: 15px 15px;
}

.login-page__dots--top {
  top: 12px;
  left: 48px;
}

.login-page__dots--bottom {
  bottom: 74px;
  left: 160px;
  opacity: 0.34;
}

.login-back-button {
  position: absolute;
  top: 32px;
  left: 48px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid rgba(27, 39, 62, 0.12);
  border-radius: 999px;
  color: #1b273e !important;
  background: rgba(255, 255, 255, 0.72);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 5px 18px rgba(27, 39, 62, 0.06);
  transition: background 0.2s ease, transform 0.2s ease;
}

.login-back-button:hover {
  background: #ffffff;
  transform: translateY(-1px);
}

.login-layout {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  flex-direction: column;
  width: min(604px, calc(100% - 64px));
  min-height: 100vh;
  margin: 0 auto;
  padding: 154px 0 38px;
}

.login-card {
  width: 604px;
  min-height: 688px;
  padding: 47px 58px 43px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 11px 30px rgba(27, 39, 62, 0.09);
}

.login-card__header {
  display: flex;
  align-items: center;
  flex-direction: column;
}

.login-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 104px;
  height: 104px;
  overflow: hidden;
  border-radius: 50%;
  background: #ffffff;
}

.login-mark img {
  display: block;
  width: 64px;
  max-width: 64px;
  height: 64px;
  margin: 0;
  border-radius: 50%;
  overflow: hidden;
  clip-path: circle(50%);
  object-fit: contain;
  object-position: center;
  position: relative;
  top: auto;
}

.login-mark--association img {
  width: 100%;
  max-width: 100%;
  height: 100%;
  margin: 0;
  object-fit: cover;
  border-radius: 50%;
  transform: scale(1.18);
  transform-origin: center;
}

.login-mark-flip-enter-active,
.login-mark-flip-leave-active {
  transition: transform 0.5s ease;
  backface-visibility: hidden;
  border-radius: 50%;
  overflow: hidden;
  clip-path: circle(50%);
}

.login-mark-flip-enter,
.login-mark-flip-leave-to {
  transform: rotateY(90deg) !important;
}

.login-card__header h2 {
  margin: 25px 0 3px;
  color: #202020;
  font-size: 31px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.6px;
}

.login-card__header p {
  margin: 0;
  color: #5f6570;
  font-size: 16px;
  font-weight: 500;
}

.login-form {
  margin-top: 27px;
}

.login-field + .login-field {
  margin-top: 21px;
}

.login-field label {
  display: block;
  margin-bottom: 8px;
  color: #4d5565;
  font-size: 14px;
  font-weight: 500;
}

.login-field__control {
  display: flex;
  align-items: center;
  height: 55px;
  padding: 0 16px;
  border: 1px solid #e4e6ea;
  border-radius: 9px;
  background: #ffffff;
  box-shadow: 0 2px 7px rgba(27, 39, 62, 0.08);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.login-field__control:focus-within {
  border-color: #24b2da;
  box-shadow: 0 0 0 3px rgba(36, 178, 218, 0.13);
}

.login-field__control--error {
  border-color: #ba1a1a;
}

.login-field__control > .v-icon {
  flex: 0 0 auto;
  color: #9ba4b2;
  font-size: 22px;
}

.login-field__control input {
  min-width: 0;
  flex: 1;
  height: 100%;
  margin-left: 16px;
  border: 0;
  outline: 0;
  color: #1b273e;
  background: transparent;
  font-size: 15px;
}

.login-field__control input::placeholder {
  color: #aeb5c1;
  opacity: 1;
}

.login-field__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  width: 24px;
  height: 32px;
  margin-left: 8px;
  color: #9ba4b2;
}

.login-field__toggle:hover {
  color: #1b273e;
}

.login-field__toggle .v-icon {
  font-size: 20px;
}

.login-field__error {
  display: block;
  margin-top: 5px;
  color: #ba1a1a;
  font-size: 12px;
}

.login-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 19px;
}

.login-remember {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #626a79;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
}

.login-remember input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.login-remember__box {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 1px solid #d2d7df;
  border-radius: 4px;
  background: #ffffff;
}

.login-remember input:checked + .login-remember__box {
  border-color: #24b2da;
  background: #24b2da;
  box-shadow: inset 0 0 0 3px #ffffff;
}

.login-forgot {
  color: #147fe5;
  font-size: 14px;
  font-weight: 600;
  text-align: right;
}

.login-forgot:hover {
  text-decoration: underline;
}

.login-submit,
.login-google {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
}

.login-submit {
  position: relative;
  justify-content: center;
  height: 47px;
  margin-top: 36px;
  padding: 0 22px;
  color: #101010;
  background: linear-gradient(90deg, #ffb500 0%, #ffb313 100%);
  box-shadow: 0 3px 5px rgba(255, 181, 0, 0.18);
  transition: filter 0.2s ease, transform 0.2s ease;
}

.login-submit span {
  white-space: nowrap;
}

.login-submit .v-icon {
  position: absolute;
  right: 22px;
  color: #101010;
  font-size: 23px;
}

.login-submit:not(:disabled):hover {
  filter: brightness(1.03);
  transform: translateY(-1px);
}

.login-submit:disabled {
  cursor: not-allowed;
  opacity: 1;
}

.login-divider {
  display: flex;
  align-items: center;
  gap: 26px;
  margin: 33px 0 28px;
  color: #596273;
}

.login-divider span {
  height: 1px;
  flex: 1;
  background: #e2e5ea;
}

.login-divider small {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.login-google-wrapper {
  display: block;
  width: 100%;
}

.login-google {
  height: 48px;
  border: 1px solid #e9ebef;
  color: #4c5360;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(27, 39, 62, 0.07);
}

.login-google:disabled {
  cursor: not-allowed;
  opacity: 0.78;
}

.login-google__mark {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  margin-right: 17px;
  object-fit: contain;
}

.login-terms {
  width: 100%;
  margin: 32px 0 0;
  color: #687283;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
}

.login-terms a {
  margin: 0 4px;
  color: #147fe5;
  text-decoration: underline;
}

@media (max-width: 820px) {
  .login-page {
    overflow: auto;
  }

  .login-back-button {
    top: 18px;
    right: auto;
    left: 20px;
    padding: 8px 12px;
    font-size: 12px;
  }

  .login-layout {
    width: min(604px, calc(100% - 32px));
    min-height: auto;
    padding: 86px 0 32px;
  }

  .login-card {
    width: 100%;
    min-height: 0;
  }

  .login-terms {
    margin-top: 22px;
  }

  .login-page__dots--bottom {
    display: none;
  }
}

@media (max-width: 520px) {
  .login-layout {
    width: calc(100% - 24px);
    padding-top: 76px;
  }

  .login-back-button {
    top: 18px;
    left: 20px;
    right: auto;
    padding: 8px 12px;
  }

  .login-card {
    padding: 36px 22px 30px;
    border-radius: 14px;
  }

  .login-card__header h2 {
    font-size: 28px;
  }

  .login-options {
    align-items: flex-start;
    flex-direction: column;
  }

  .login-forgot {
    align-self: flex-end;
  }

  .login-submit {
    padding: 0 22px;
  }

  .login-divider {
    gap: 12px;
  }

  .login-terms {
    padding: 0 8px;
    font-size: 12px;
  }
}
</style>
